import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';

export default function Home() {
  const [scrollY, setScrollY] = useState(0);
  const [aboutSectionImage] = useState('https://static.readdy.ai/image/ba36a1534eff85bb13d578944b3dbf04/4631baa49b2b8ab2195d6d51b8f28627.png');
  const [currentSlide, setCurrentSlide] = useState(0);

  const heroImages = [
    'https://static.readdy.ai/image/ba36a1534eff85bb13d578944b3dbf04/e54b75a013644b39ea8d3f8a41765612.jpeg',
    'https://static.readdy.ai/image/ba36a1534eff85bb13d578944b3dbf04/3983f7b5e0a38267a25ecd9223182385.png',
    'https://static.readdy.ai/image/ba36a1534eff85bb13d578944b3dbf04/0600287f1b41b750e9ff1b205a9a2ee8.png',
    'https://static.readdy.ai/image/ba36a1534eff85bb13d578944b3dbf04/405f2a7d2f5c2a679f35f7ac209b426a.png'
  ];

  // Auto slide
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 15000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
  }, []);

  const goToPrevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);
  }, [heroImages.length]);

  const goToNextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
  }, [heroImages.length]);

  // Cache Storage helpers
  const ABOUT_CACHE_NAME = 'about-image-cache';

  const saveAboutImageToCache = async (imageUrl: string) => {
    try {
      const cache = await caches.open(ABOUT_CACHE_NAME);
      const response = new Response(imageUrl);
      await cache.put('/about-image', response);
    } catch (error) {
      console.error('Failed to save about image to cache:', error);
    }
  };

  const loadAboutImageFromCache = async () => {
    try {
      const cache = await caches.open(ABOUT_CACHE_NAME);
      const response = await cache.match('/about-image');
      if (response) {
        const imageUrl = await response.text();
        // Image loading from cache removed since editing functionality is removed
      }
    } catch (error) {
      console.error('Failed to load about image from cache:', error);
    }
  };

  // Load saved images from Cache Storage
  useEffect(() => {
    const loadImages = async () => {
      const savedAboutImage = localStorage.getItem('aboutSectionImage');
      
      if (savedAboutImage) {
        await saveAboutImageToCache(savedAboutImage);
        localStorage.removeItem('aboutSectionImage');
      }

      await loadAboutImageFromCache();
    };

    loadImages();
  }, []);

  const handleLinkClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAboutImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const result = e.target?.result as string;
        // Image upload functionality removed
        await saveAboutImageToCache(result);
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    // Intersection Observer for card animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-slide-in-up');
          entry.target.classList.remove('opacity-0', 'translate-y-8');
        }
      });
    }, observerOptions);

    const cards = document.querySelectorAll('.card-animate');
    cards.forEach((card) => observer.observe(card));

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  // Calculate color transition based on scroll position
  const getTextColor = () => {
    const startScroll = window.innerHeight * 0.3;
    const endScroll = window.innerHeight * 0.8;
    const progress = Math.min(Math.max((scrollY - startScroll) / (endScroll - startScroll), 0), 1);
    
    const r = Math.round(255 - (255 - 16) * progress);
    const g = Math.round(255 - (255 - 185) * progress);
    const b = Math.round(255 - (255 - 129) * progress);
    
    return `rgb(${r}, ${g}, ${b})`;
  };

  // Scroll to next section
  const scrollToNextSection = () => {
    const nextSection = document.querySelector('section:nth-of-type(2)');
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative h-[60vh] sm:h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image Slider */}
        {heroImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={image}
              alt={`Slide ${index + 1}`}
              className="w-full h-full object-cover object-center md:object-center"
              style={{
                objectPosition: window.innerWidth < 768 ? 'center center' : 'center'
              }}
            />
          </div>
        ))}
        
        {/* Light Overlay for better text visibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/10"></div>
        
        {/* Slide Navigation Arrows */}
        <button
          onClick={goToPrevSlide}
          className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/40 transition-all duration-300 cursor-pointer group"
        >
          <i className="ri-arrow-left-s-line text-white text-2xl group-hover:scale-110 transition-transform"></i>
        </button>
        <button
          onClick={goToNextSlide}
          className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/40 transition-all duration-300 cursor-pointer group"
        >
          <i className="ri-arrow-right-s-line text-white text-2xl group-hover:scale-110 transition-transform"></i>
        </button>
        
        <div className="absolute bottom-8 md:bottom-12 left-1/2 -translate-x-1/2 z-20 w-full text-center">
          {/* Slide Indicators */}
          <div className="flex gap-3 justify-center mb-6">
            {heroImages.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                  index === currentSlide 
                    ? 'bg-white w-8' 
                    : 'bg-white/50 hover:bg-white/80'
                }`}
              />
            ))}
          </div>
          
          {/* Title below indicators */}
          <div className="animate-fade-in-left">
            <h1 
              className="text-2xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white/80 drop-shadow-[0_4px_12px_rgba(0,0,0,0.5)] tracking-wider text-center whitespace-nowrap"
              style={{ fontFamily: 'MungyeongGamhongApple, sans-serif' }}
            >
              Green Design Tech
            </h1>
          </div>
        </div>
      </section>
      
      {/* About Preview Section */}
      <section className="py-12 md:py-20 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 md:mb-16 animate-fade-in-up">
            <div className="inline-block mb-4 md:mb-6">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-emerald-400 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                <i className="ri-heart-3-fill text-white text-xl md:text-2xl"></i>
              </div>
            </div>
            <h2 
              className="text-2xl md:text-4xl font-bold mb-4 md:mb-6 transition-colors duration-300"
              style={{ color: getTextColor(), fontFamily: 'Paperozi, sans-serif' }}
            >
              <span className="block md:inline">제작부터 설치까지,</span>
              <span className="block md:inline"> 친환경 공간 솔루션의 기준</span>
            </h2>
            <h2 
              className="text-2xl md:text-4xl font-bold transition-colors duration-300"
              style={{ color: getTextColor(), fontFamily: 'Paperozi, sans-serif' }}
            >
              <span className="block md:inline">설계에서 현장까지,</span>
              <span className="block md:inline"> 책임지는 그린디자인테크</span>
            </h2>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-6 md:gap-8 items-center">
            <div 
              className="rounded-2xl md:rounded-3xl overflow-hidden shadow-[0_25px_60px_-12px_rgba(0,0,0,0.35)] transform hover:scale-105 transition-transform duration-500 relative group h-[300px] md:h-[500px]"
              style={{
                backgroundImage: `url('${aboutSectionImage}')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
            </div>
            
            <div className="space-y-4 md:space-y-6 h-full flex flex-col justify-center">
              <div className="card-animate opacity-0 translate-y-8 transition-all duration-700 ease-out bg-white rounded-xl md:rounded-2xl p-4 md:p-6 shadow-lg hover:shadow-xl">
                <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-2 md:mb-3">제작부터 설치까지</h3>
                <p className="text-gray-600 text-base leading-relaxed">환경을 배려한 제작, 사람을 위한 설치</p>
              </div>
              
              <div className="card-animate opacity-0 translate-y-8 transition-all duration-700 ease-out delay-200 bg-white rounded-xl md:rounded-2xl p-4 md:p-6 shadow-lg hover:shadow-xl">
                <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-2 md:mb-3">전문성</h3>
                <p className="text-gray-600 text-base leading-relaxed">디자인과 기술을 이해하는 전문인력이 프로젝트 전반을 체계적으로 관리합니다.</p>
              </div>
              
              <div className="card-animate opacity-0 translate-y-8 transition-all duration-700 ease-out delay-400 bg-white rounded-xl md:rounded-2xl p-4 md:p-6 shadow-lg hover:shadow-xl">
                <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-2 md:mb-3">친환경</h3>
                <p className="text-gray-600 text-base leading-relaxed">지속 가능한 소재와 공정으로 환경에 미치는 영향을 최소화합니다.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Featured Work Section */}
      
      {/* CTA Section */}
      <section className="py-12 md:py-20 px-4 md:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-gray-800 mb-4 md:mb-6">자연을 설계, 공간의 가치를 완성</h2>
          <p className="text-base md:text-xl text-gray-600 mb-6 md:mb-8 max-w-2xl mx-auto">
            Harmony of Nature and Technology
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
            <Link to="/products" onClick={handleLinkClick}>
              <Button variant="primary" size="lg">
                제품 보기
              </Button>
            </Link>
            <Link to="/catalog" onClick={handleLinkClick}>
              <Button variant="secondary" size="lg">
                카달로그 보기
              </Button>
            </Link>
            <Link 
              to="/contact"
              onClick={handleLinkClick}
              className="inline-block bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-6 md:px-8 py-3 md:py-4 rounded-full text-base md:text-lg font-semibold hover:from-emerald-600 hover:to-teal-600 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
            >
              견적 문의
            </Link>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
